import { RdwCategoryProfileEnum, type RdwCategoryProfile, type RdwVehicleFacts } from './rdw.types';

/**
 * Maps an RDW lookup onto an asset-category profile.
 *
 * An asset category (module `assets-category`) carries the profile it is linked to; after a
 * licence-plate lookup the category is pre-filled with the category that claims the resolved
 * profile. Own category names stay free — the profile is only the join key.
 *
 * Fourteen of the fifteen RDW `voertuigsoort` values map straight through. `Bedrijfsauto` covers
 * 1.5M vehicles from van to tractor unit, so it is split further:
 *
 *   Bedrijfsauto + N1                                    -> bestelbus  (<= 3,5 t)
 *   Bedrijfsauto + N2/N3, inrichting != opleggertrekker  -> bakwagen
 *   Bedrijfsauto + N3,    inrichting == opleggertrekker  -> trekker
 *
 * Not derivable from RDW, and deliberately absent here: a box truck under 3,5 t ("meubelbak") is
 * registered exactly like a van (N1, gesloten opbouw, EU body code BB), and ownership variants
 * such as a customer-owned trailer carry no RDW signal at all. Those categories are left unlinked
 * so the user picks them by hand.
 */

/**
 * Sub-key the resolved profile is stored under, inside the RDW field's own object value.
 *
 * A `copyFieldsOnSelect` query-mapping points its dynamic filter at this key to find the
 * asset-category that claims the profile, so the name is part of the configuration contract.
 */
export const RDW_CATEGORY_PROFILE_KEY = 'categoryProfile';

/** RDW response fields required to derive {@link RDW_CATEGORY_PROFILE_KEY}. */
export const RDW_CATEGORY_API_FIELDS = {
  vehicleType: 'voertuigsoort',
  europeanVehicleCategory: 'europese_voertuigcategorie',
  bodyType: 'inrichting',
} as const;

/** Keep profile inputs mapped even when an author does not show them as vehicle fields. */
export function includeRdwCategoryApiFields(vehicleFields: readonly string[]): string[] {
  return [...new Set([...vehicleFields, ...Object.values(RDW_CATEGORY_API_FIELDS)])];
}

/**
 * The profile list as it has to exist in configuration, in the order an author reads it.
 *
 * The join itself lives in the module config, not in code: module `assets-category` carries a
 * manual-options select `rdwCategory` whose option values are exactly these slugs, and the RDW
 * field's `copyFieldsOnSelect` query-mapping compares `entry.rdwCategory` against
 * `<rdw-field>.categoryProfile`. Rolling the feature out to another tenant means recreating that
 * select from this list — hence keeping it here, machine-readable, rather than only in a doc.
 *
 * A profile added to `RdwCategoryProfileEnum` without an entry here is caught by the spec.
 */
export const RDW_CATEGORY_PROFILE_OPTIONS: Array<{ value: RdwCategoryProfile; label: string }> = [
  { value: RdwCategoryProfileEnum.Personenauto, label: 'Personenauto' },
  { value: RdwCategoryProfileEnum.Bestelbus, label: 'Bestelbus (t/m 3,5 t)' },
  { value: RdwCategoryProfileEnum.Bakwagen, label: 'Bakwagen (boven 3,5 t)' },
  { value: RdwCategoryProfileEnum.Trekker, label: 'Trekker' },
  { value: RdwCategoryProfileEnum.Oplegger, label: 'Oplegger' },
  { value: RdwCategoryProfileEnum.Aanhangwagen, label: 'Aanhangwagen' },
  { value: RdwCategoryProfileEnum.Middenasaanhangwagen, label: 'Middenasaanhangwagen' },
  { value: RdwCategoryProfileEnum.AutonomeAanhangwagen, label: 'Autonome aanhangwagen' },
  { value: RdwCategoryProfileEnum.Bus, label: 'Bus' },
  { value: RdwCategoryProfileEnum.Motorfiets, label: 'Motorfiets' },
  { value: RdwCategoryProfileEnum.MotorfietsMetZijspan, label: 'Motorfiets met zijspan' },
  { value: RdwCategoryProfileEnum.Bromfiets, label: 'Bromfiets' },
  { value: RdwCategoryProfileEnum.DriewieligMotorrijtuig, label: 'Driewielig motorrijtuig' },
  { value: RdwCategoryProfileEnum.LandOfBosbouwtrekker, label: 'Land- of bosbouwtrekker' },
  { value: RdwCategoryProfileEnum.LandOfBosbouwAanhanger, label: 'Land- of bosbouwaanhangwagen' },
  { value: RdwCategoryProfileEnum.MotorrijtuigMetBeperkteSnelheid, label: 'Motorrijtuig met beperkte snelheid' },
  { value: RdwCategoryProfileEnum.MobieleMachine, label: 'Mobiele machine' },
];

/** RDW `voertuigsoort` values that need no further narrowing. */
const VOERTUIGSOORT_PROFILES: Record<string, RdwCategoryProfile> = {
  personenauto: RdwCategoryProfileEnum.Personenauto,
  oplegger: RdwCategoryProfileEnum.Oplegger,
  aanhangwagen: RdwCategoryProfileEnum.Aanhangwagen,
  middenasaanhangwagen: RdwCategoryProfileEnum.Middenasaanhangwagen,
  'autonome aanhangwagen': RdwCategoryProfileEnum.AutonomeAanhangwagen,
  bus: RdwCategoryProfileEnum.Bus,
  motorfiets: RdwCategoryProfileEnum.Motorfiets,
  'motorfiets met zijspan': RdwCategoryProfileEnum.MotorfietsMetZijspan,
  bromfiets: RdwCategoryProfileEnum.Bromfiets,
  'driewielig motorrijtuig': RdwCategoryProfileEnum.DriewieligMotorrijtuig,
  'land- of bosbouwtrekker': RdwCategoryProfileEnum.LandOfBosbouwtrekker,
  'land- of bosb aanhw of getr uitr stuk': RdwCategoryProfileEnum.LandOfBosbouwAanhanger,
  'motorrijtuig met beperkte snelheid': RdwCategoryProfileEnum.MotorrijtuigMetBeperkteSnelheid,
  'mobiele machine': RdwCategoryProfileEnum.MobieleMachine,
};

/** RDW `inrichting` value that marks a tractor unit. */
const TRACTOR_UNIT_BODY = 'opleggertrekker';

function normalize(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/**
 * Resolve the profile for a looked-up vehicle, or null when RDW gives no usable answer.
 *
 * Null is a normal outcome, not an error: an unknown vehicle type, or a `Bedrijfsauto` whose
 * European category is missing, leaves the category empty so the user chooses it.
 */
export function resolveRdwCategoryProfile(facts: RdwVehicleFacts): RdwCategoryProfile | null {
  const soort = normalize(facts.voertuigsoort);
  if (!soort) {
    return null;
  }

  const direct = VOERTUIGSOORT_PROFILES[soort];
  if (direct) {
    return direct;
  }

  if (soort !== 'bedrijfsauto') {
    return null;
  }

  // European category carries an optional 'G' suffix for off-road versions (N1G, N3G), so match
  // on the prefix rather than the exact code.
  const category = normalize(facts.europeseVoertuigcategorie);
  if (category.startsWith('n1')) {
    return RdwCategoryProfileEnum.Bestelbus;
  }
  if (category.startsWith('n3')) {
    return normalize(facts.inrichting) === TRACTOR_UNIT_BODY
      ? RdwCategoryProfileEnum.Trekker
      : RdwCategoryProfileEnum.Bakwagen;
  }
  if (category.startsWith('n2')) {
    // N2 tractor units exist but are rare; the body type decides here too.
    return normalize(facts.inrichting) === TRACTOR_UNIT_BODY
      ? RdwCategoryProfileEnum.Trekker
      : RdwCategoryProfileEnum.Bakwagen;
  }

  return null;
}
